import { RouteResponse } from "@0xsquid/sdk/dist/types";
import {
  ActionType,
  BridgeProvider,
  ChainData,
  ChainType,
  Estimate,
  FeeCost,
  GasCost,
  Token,
} from "@0xsquid/squid-types";
import {
  convertTokenAmountToUSD,
  findToken,
  formatSeconds,
  simplifyRouteAction,
} from "..";
import { gasRefundMultiplier } from "../../core/constants";
import { SimplifiedRouteAction } from "../../core/types/transaction";
import { formatBNToReadable, formatUnitsRounded } from "./numberService";
import { ChainFeeParams } from "./tempoService";

export type PartialFeeCost = Pick<FeeCost, "amount"> & {
  token?: Pick<FeeCost["token"], "decimals">;
};

export const formatAmount = (
  amountInWei: string | undefined,
  decimals: number | undefined
) => (amountInWei ? formatBNToReadable(amountInWei, decimals) : "");

export const parseToAmountUSD = (toAmountUSD: string | undefined) =>
  toAmountUSD ? parseFloat(toAmountUSD.replace(/,/g, "")) : 0;

const CHAIN_TYPES_SUPPORTING_GAS_REFUND: ChainType[] = [
  ChainType.EVM,
  ChainType.SUI,
  ChainType.STELLAR,
];

/**
 * Calculates the expected gas refund
 *
 * Currently we only refund when source chain supports gas refunds
 * and Axelar is one of the bridge actions
 */
export const calculateExpectedGasRefundCost = (
  firstFeeCost: PartialFeeCost | undefined,
  fromChainType: ChainType | undefined,
  actions: SimplifiedRouteAction[] | undefined
): bigint => {
  if (
    !fromChainType ||
    !CHAIN_TYPES_SUPPORTING_GAS_REFUND.includes(fromChainType)
  ) {
    return BigInt(0);
  }

  const containsAxelarBridgeAction = actions?.some(
    (action) =>
      action.type === ActionType.BRIDGE &&
      action.provider === BridgeProvider.AXELAR
  );

  if (!containsAxelarBridgeAction) {
    return BigInt(0);
  }

  const amount = BigInt(firstFeeCost?.amount ?? "0");
  const refundMultiplier = BigInt(gasRefundMultiplier);
  return (amount * refundMultiplier) / BigInt(100);
};

export const calculateTotalNativeFees = ({
  expressFeeCost,
  firstFeeCost,
  firstGasCost,
  sameTokenBetweenFees,
}: {
  expressFeeCost: Pick<FeeCost, "amount"> | undefined;
  firstFeeCost: Pick<FeeCost, "amount"> | undefined;
  firstGasCost: Pick<GasCost, "amount"> | undefined;
  sameTokenBetweenFees: boolean;
}) =>
  BigInt(expressFeeCost?.amount ?? "0") +
  (sameTokenBetweenFees
    ? BigInt(firstFeeCost?.amount ?? "0") + BigInt(firstGasCost?.amount ?? "0")
    : BigInt(firstGasCost?.amount ?? "0"));

export const checkIsGasBalanceEnough = ({
  fromAmount,
  networkFeesWei,
  chainFeeParams,
}: {
  fromAmount: string | undefined;
  networkFeesWei: bigint;
  chainFeeParams: ChainFeeParams | null;
}): boolean => {
  if (!chainFeeParams) return false;

  if (chainFeeParams.fromTokenPaysGasFees) {
    return (
      BigInt(fromAmount ?? "0") + networkFeesWei <=
      chainFeeParams.gasTokenBalanceWei
    );
  }

  return networkFeesWei <= chainFeeParams.gasTokenBalanceWei;
};

export const calculateMinAmountValueWarnMsg = ({
  networkFeesWei,
  chainFeeParams,
  fromTokenDecimals,
}: {
  networkFeesWei: bigint;
  chainFeeParams: ChainFeeParams | null;
  fromTokenDecimals: number | undefined;
}): string | undefined => {
  if (!chainFeeParams?.fromTokenPaysGasFees) return undefined;

  const minAmount = chainFeeParams.gasTokenBalanceWei - networkFeesWei;

  if (minAmount <= BigInt(0)) return undefined;

  return formatBNToReadable(minAmount, fromTokenDecimals);
};

/**
 * Calculates the estimated duration of a route
 *
 * @param {number} estimatedRouteDuration - The estimated route duration in seconds
 * @param {boolean} isSingleChainRoute - Flag indicating whether the route is single chain
 *
 * @returns {Object} An object containing the estimated duration and a formatter function
 */
export const formatEstimatedRouteDuration = ({
  estimatedRouteDuration,
  isSingleChainRoute,
}: {
  estimatedRouteDuration?: number;
  isSingleChainRoute: boolean;
}) => {
  const fallbackDuration = 20;
  let durationInSeconds = fallbackDuration;
  if (estimatedRouteDuration) {
    durationInSeconds = Math.round(estimatedRouteDuration);
  } else if (isSingleChainRoute) {
    durationInSeconds = 1;
  } else {
    durationInSeconds = fallbackDuration;
  }
  return {
    seconds: durationInSeconds,
    format: (sTemplate?: string, mTemplate?: string, hTemplate?: string) =>
      formatSeconds(durationInSeconds, sTemplate, mTemplate, hTemplate),
  };
};

/**
 * Calculates and formats various estimate results based on the provided Squid route and additional parameters.
 *
 * @param {RouteResponse["route"]} squidRoute - The Squid route containing estimate information.
 * @param {Token[]} tokens - An array of token objects.
 * @param {ChainData} fromChain - The source chain object.
 * @param {ChainData} toChain - The destination chain object.
 * @param {boolean} collectFees - Flag indicating whether to collect fees.
 * @param {string} nativeTokenBalanceFromChain - The native token balance on the source chain.
 * @param {boolean} expressActivatedUI - Flag indicating whether express mode is activated in the UI.
 * @returns {Object} An object containing various estimate results and calculations, including token information,
 *                   amounts, fees, gas costs, and other relevant data for the transaction.
 */
export type EstimateRouteInput = {
  params: Pick<RouteResponse["route"]["params"], "fromChain" | "fromToken">;
  estimate: Pick<
    Estimate,
    | "fromAmount"
    | "toAmount"
    | "toAmountMin"
    | "toAmountUSD"
    | "toAmountMinUSD"
    | "exchangeRate"
    | "estimatedRouteDuration"
    | "feeCosts"
  > & {
    toToken: Pick<Token, "decimals">;
    gasCosts: (Omit<GasCost, "token"> & {
      token: Pick<Token, "chainId" | "address" | "decimals">;
    })[];
    actions?: Estimate["actions"];
  };
};

export const calculateEstimateResults = <
  T extends Pick<Token, "chainId" | "address" | "decimals"> = Token,
>({
  squidRoute,
  tokens,
  fromChain,
  toChain,
  collectFees,
  chainFeeParams,
  gasToken,
}: {
  squidRoute: EstimateRouteInput | undefined;
  tokens: T[];
  fromChain?: Pick<ChainData, "chainId" | "chainType">;
  toChain?: Pick<ChainData, "chainId" | "chainType">;
  collectFees: boolean;
  chainFeeParams: ChainFeeParams | null;
  gasToken: Pick<Token, "decimals"> | undefined;
}) => {
  const fromToken = findToken(
    tokens,
    squidRoute?.params.fromChain,
    squidRoute?.params.fromToken
  );
  const fromAmount = squidRoute?.estimate?.fromAmount;
  const fromAmountFormatted = formatAmount(fromAmount, fromToken?.decimals);
  const toAmountUSD = squidRoute?.estimate?.toAmountUSD;
  const exchangeRate = squidRoute?.estimate.exchangeRate ?? "0";
  const toAmountMinUSD = squidRoute?.estimate.toAmountMinUSD ?? "0";

  const toAmountMin = formatBNToReadable(
    squidRoute?.estimate.toAmountMin ?? "0",
    squidRoute?.estimate.toToken.decimals
  );

  const toAmount = formatUnitsRounded(
    (squidRoute?.estimate.toAmount ?? "0").toString(),
    squidRoute?.estimate.toToken.decimals,
    14
  );

  const estimate = squidRoute?.estimate as Estimate | undefined;
  const allFeeCosts: FeeCost[] = estimate?.feeCosts ?? [];
  const allGasCosts: GasCost[] = estimate?.gasCosts ?? [];
  const firstFeeCost = allFeeCosts.length > 0 ? allFeeCosts[0] : undefined;
  const firstGasCost = allGasCosts.length > 0 ? allGasCosts[0] : undefined;
  const expressFeeCost = allFeeCosts.find((f) => f.name === "Boost fee");
  const integratorFeeCost =
    allFeeCosts.length > 0 && collectFees
      ? allFeeCosts.find((f) => f.name === "Integrator fee")
      : undefined;

  const expectedGasRefundCost = calculateExpectedGasRefundCost(
    firstFeeCost,
    fromChain?.chainType,
    estimate?.actions?.map(simplifyRouteAction)
  );

  const expectedGasRefundCostUSD = convertTokenAmountToUSD(
    formatBNToReadable(
      expectedGasRefundCost,
      firstFeeCost?.token.decimals ?? 18
    ),
    firstFeeCost?.token.usdPrice ?? "0"
  );

  const sameTokenBetweenFees =
    firstFeeCost?.token.address === firstGasCost?.token.address &&
    firstFeeCost?.token.chainId === firstGasCost?.token.chainId;

  const totalNativeFees = calculateTotalNativeFees({
    expressFeeCost,
    firstFeeCost,
    firstGasCost,
    sameTokenBetweenFees,
  });

  const totalFeesInNativeTokenPlusRatio =
    (totalNativeFees * BigInt(110)) / BigInt(100);

  const networkFeesWei =
    chainFeeParams?.normalizeFee(totalFeesInNativeTokenPlusRatio) ?? BigInt(0);

  const isGasBalanceEnough = checkIsGasBalanceEnough({
    chainFeeParams,
    fromAmount,
    networkFeesWei,
  });

  const minAmountValueWarnMsg = calculateMinAmountValueWarnMsg({
    chainFeeParams,
    networkFeesWei,
    fromTokenDecimals: fromToken?.decimals,
  });

  const isSingleChainRoute = fromChain?.chainId === toChain?.chainId;

  const estimatedRouteDuration = formatEstimatedRouteDuration({
    estimatedRouteDuration: squidRoute?.estimate?.estimatedRouteDuration,
    isSingleChainRoute,
  });

  // gas fees + fromAmount (if fromToken is gas token)
  const totalGasBalanceNeeded =
    networkFeesWei +
    BigInt(chainFeeParams?.fromTokenPaysGasFees ? (fromAmount ?? 0) : 0);

  const gasBalanceNeeded = gasToken
    ? formatBNToReadable(totalGasBalanceNeeded, gasToken.decimals)
    : undefined;

  return {
    fromToken,
    fromAmount,
    fromAmountFormatted,
    toAmountUSD,
    exchangeRate,
    toAmountMinUSD,
    toAmountMin,
    toAmount,
    allFeeCosts,
    allGasCosts,
    firstFeeCost,
    firstGasCost,
    expressFeeCost,
    integratorFeeCost,
    expectedGasRefundCost,
    expectedGasRefundCostUSD,
    sameTokenBetweenFees,
    fromTokenPaysGasFees: chainFeeParams?.fromTokenPaysGasFees ?? false,
    totalNativeFees,
    isGasBalanceEnough,
    minAmountValueWarnMsg,
    estimatedRouteDuration,
    gasBalanceNeeded,
  };
};

/**
 * Calculates the total estimate with refund for a transaction.
 *
 * @param allFeeCosts - Total fee costs of the transaction
 * @param expectedGasRefundCost - The expected gas refund cost as a bigint.
 * @param getUSDValue - A function to convert an amount to its USD value.
 * @returns An object containing the total amount, total amount in USD, and the fee token.
 */
export const calculateTotalWithRefundEstimate = (
  allFeeCosts: FeeCost[] | undefined,
  expectedGasRefundCost: bigint,
  getUSDValue: (amount: string) => string
) => {
  const initialTotalFees = {
    totalFeeAmount: BigInt(0),
    totalFeeUsd: 0,
  };

  const { totalFeeAmount, totalFeeUsd } =
    allFeeCosts?.reduce((totalFees, currentFee) => {
      return {
        totalFeeAmount:
          BigInt(totalFees.totalFeeAmount) + BigInt(currentFee.amount),
        totalFeeUsd:
          Number(totalFees.totalFeeUsd) + Number(currentFee.amountUsd),
      };
    }, initialTotalFees) ?? initialTotalFees;

  const totalFeeAmountMinusRefund = totalFeeAmount - expectedGasRefundCost;
  const firstFeeCost = allFeeCosts?.[0];
  const totalFeeAmountMinusRefundBn = formatBNToReadable(
    totalFeeAmountMinusRefund,
    firstFeeCost?.token?.decimals ?? 18
  );
  const formattedRefundCost = formatBNToReadable(
    expectedGasRefundCost,
    firstFeeCost?.token.decimals ?? 18
  );

  const formattedRefundCostUsd = getUSDValue(formattedRefundCost);
  const totalFeeUsdMinusRefund =
    totalFeeUsd - Number(formattedRefundCostUsd ?? 0);

  return {
    totalAmount: totalFeeAmountMinusRefundBn,
    totalAmountUSD: totalFeeUsdMinusRefund,
    feeToken: firstFeeCost?.token,
  };
};
